import { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';

import {
  deleteUserStorageItems,
  getUserStorageItems,
  saveUserStorageItems,
} from '@/api/userStorageItems';
import { adaptMutation } from '@/modules/common/helpers/swr';
import { useInfoProject } from '@/modules/common/hooks/useInfoProject';
import { useGetCommonStore } from '@/store';
import {
  type SERVER_USER_STORAGE_IDENTIFIER,
  SWR_USER_STORAGE_ITEMS_KEY,
} from './helpers';

/**
 *
 * @param identifier - Optional identifier to filter the returned items by.
 * @description Reads the player's server-persisted storage items via SWR.
 * Disabled when the player is not authenticated or the feature flag is off.
 */
export const useUserStorageItems = <T = Record<string, unknown>>(
  identifier?: SERVER_USER_STORAGE_IDENTIFIER
) => {
  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const { data: infoProject } = useInfoProject();

  const swrKey =
    isAuthenticated && infoProject?.userStorageItems
      ? SWR_USER_STORAGE_ITEMS_KEY
      : null;

  const { data, error, isLoading, mutate } = useSWRImmutable(
    swrKey,
    async () => {
      const response = await getUserStorageItems<T>();
      if (response?.error) throw response.error;
      return response.data?.data;
    }
  );

  const userStorageItem = identifier
    ? data?.find((item) => item.identifier === identifier)
    : undefined;

  return {
    userStorageItems: data,
    userStorageItem,
    error,
    isLoading,
    refetch: mutate,
  };
};

export const useSaveUserStorageItems = () => {
  const { mutate: globalMutate } = useSWRConfig();

  const { trigger } = useSWRMutation(
    'saveUserStorageItems',
    adaptMutation(saveUserStorageItems),
    { onSuccess: async () => globalMutate(SWR_USER_STORAGE_ITEMS_KEY) }
  );

  return { saveUserStorageItems: trigger };
};

export const useDeleteUserStorageItems = () => {
  const { mutate: globalMutate } = useSWRConfig();

  const { trigger } = useSWRMutation(
    'deleteUserStorageItems',
    adaptMutation(deleteUserStorageItems),
    { onSuccess: async () => globalMutate(SWR_USER_STORAGE_ITEMS_KEY) }
  );

  return { deleteUserStorageItems: trigger };
};
